import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/store";
import { useSampleSchedule, useDeleteSampleSchedule } from "@/hooks/use-sample-schedules";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ScheduleOverviewTab } from "@/components/schedules/schedule-overview-tab";
import { ScheduleEventsTab } from "@/components/schedules/schedule-events-tab";
import { ScheduleGapsTab } from "@/components/schedules/schedule-gaps-tab";
import { PageHeader, Breadcrumbs, PageSkeleton } from "@/components/ui";
import { Tabs, type TabDef } from "@/components/ui/tabs";

type ScheduleTab = "overview" | "events" | "gaps";

export const Route = createFileRoute("/_authenticated/schedules/$scheduleId")({
  component: ScheduleDetailPage,
});

function ScheduleDetailPage() {
  const { t } = useTranslation(["schedules", "common"]);
  const navigate = useNavigate();
  const { scheduleId } = Route.useParams();
  const orgId = useAppStore((s) => s.activeOrgId);

  const { data: schedule, isLoading } = useSampleSchedule(orgId, scheduleId);
  useRecentPageTitle(schedule?.name ?? undefined);
  const deleteMutation = useDeleteSampleSchedule(orgId);

  const [activeTab, setActiveTab] = useState<ScheduleTab>("overview");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    deleteMutation.mutate(scheduleId, {
      onSuccess: () => void navigate({ to: "/schedules" }),
    });
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!schedule) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("schedules:errors.loadFailed")}</p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[{ label: t("schedules:title"), to: "/schedules" }, { label: schedule.name }]}
      />

      <PageHeader title={schedule.name} subtitle={schedule.description ?? undefined} />

      <div className="mt-6">
        <Tabs
          tabs={
            [
              { value: "overview", label: t("schedules:tabs.overview") },
              { value: "events", label: t("schedules:tabs.events") },
              { value: "gaps", label: t("schedules:tabs.gaps") },
            ] satisfies TabDef<ScheduleTab>[]
          }
          activeTab={activeTab}
          onTabChange={setActiveTab}
          ariaLabel={t("schedules:detailTitle")}
        />

        <div
          className="mt-4"
          role="tabpanel"
          id={`tabpanel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === "overview" && (
            <ScheduleOverviewTab
              schedule={schedule}
              orgId={orgId}
              onDelete={() => setShowDeleteConfirm(true)}
            />
          )}
          {activeTab === "events" && (
            <ScheduleEventsTab
              orgId={orgId}
              scheduleId={scheduleId}
              scheduleStatus={schedule.status}
            />
          )}
          {activeTab === "gaps" && (
            <ScheduleGapsTab orgId={orgId} scheduleId={scheduleId} schedule={schedule} />
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        title={t("schedules:confirm.deleteTitle")}
        message={t("schedules:confirm.deleteMessage")}
        confirmLabel={t("common:delete")}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  );
}
